import { Card, Table, Tag, Button, Modal, Form, Input, Select, Radio, message, Tooltip, Empty } from 'antd';
import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../utils/request';
import { useTranslation } from '../../language';
import './Billing.css';

interface Order {
  id: string;
  orderNo: string;
  planName: string;
  amount: number;
  discountAmount: number;
  finalAmount: number;
  status: number;
  paymentMethod?: string;
  paymentTime?: string;
  startTime?: string;
  endTime?: string;
  createTime: string;
  invoiceStatus?: number;
}

interface Invoice {
  id: string;
  invoiceNo: string;
  orderNo: string;
  amount: number;
  title: string;
  taxNo?: string;
  billingType: string;
  content?: string;
  recipientName?: string;
  recipientPhone?: string;
  recipientAddress?: string;
  status: number;
  reviewComment?: string;
  issuedTime?: string;
  createTime: string;
}

export default function Billing() {
  const { t } = useTranslation();
  const [view, setView] = useState<'orders' | 'invoices'>('orders');

  const ORDER_STATUS: Record<number, { color: string; text: string }> = {
    1: { color: 'orange', text: t('menu.billingNamespace.statusPending') },
    2: { color: 'green', text: t('menu.billingNamespace.statusPaid') },
    3: { color: 'blue', text: t('menu.billingNamespace.statusCompleted') },
    4: { color: 'default', text: t('menu.billingNamespace.statusCancelled') },
    5: { color: 'red', text: t('menu.billingNamespace.statusRefunded') },
  };

  const INVOICE_STATUS: Record<number, { color: string; text: string }> = {
    1: { color: 'orange', text: t('menu.billingNamespace.statusPendingReview') },
    2: { color: 'blue', text: t('menu.billingNamespace.statusApproved') },
    3: { color: 'red', text: t('menu.billingNamespace.statusRejected') },
    4: { color: 'green', text: t('menu.billingNamespace.statusIssued') },
    5: { color: 'default', text: t('menu.billingNamespace.statusVoided') },
  };

  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const ordersLoadingRef = useRef(false);
  const [orderKeyword, setOrderKeyword] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState<number | undefined>(undefined);

  // Invoices state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const invoicesLoadingRef = useRef(false);
  const [invoiceKeyword, setInvoiceKeyword] = useState('');

  const orderKeywordRef = useRef(orderKeyword);
  const orderStatusFilterRef = useRef(orderStatusFilter);
  const invoiceKeywordRef = useRef(invoiceKeyword);

  useEffect(() => { orderKeywordRef.current = orderKeyword; }, [orderKeyword]);
  useEffect(() => { orderStatusFilterRef.current = orderStatusFilter; }, [orderStatusFilter]);
  useEffect(() => { invoiceKeywordRef.current = invoiceKeyword; }, [invoiceKeyword]);

  // Invoice modal
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [invoiceForm] = Form.useForm();
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);

  // Invoice detail modal
  const [invoiceDetailVisible, setInvoiceDetailVisible] = useState(false);
  const [invoiceDetailRecord, setInvoiceDetailRecord] = useState<Invoice | null>(null);

  const loadOrders = useCallback(async () => {
    if (ordersLoadingRef.current) return;
    ordersLoadingRef.current = true;
    setOrdersLoading(true);
    try {
      const data = await api.get<any>('/orders');
      const kw = orderKeywordRef.current;
      const status = orderStatusFilterRef.current;
      let list: Order[] = data.records || [];
      if (kw) {
        const lckw = kw.toLowerCase();
        list = list.filter(o => o.orderNo?.toLowerCase().includes(lckw) || o.planName?.toLowerCase().includes(lckw));
      }
      if (status !== undefined) {
        list = list.filter(o => o.status === status);
      }
      setOrders(list);
    } catch (err: any) {
      console.error('Failed to load orders:', err);
    } finally {
      setOrdersLoading(false);
      ordersLoadingRef.current = false;
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    if (invoicesLoadingRef.current) return;
    invoicesLoadingRef.current = true;
    setInvoicesLoading(true);
    try {
      const data = await api.get<Invoice[]>('/invoices');
      const kw = invoiceKeywordRef.current;
      let list: Invoice[] = Array.isArray(data) ? data : [];
      if (kw) {
        const lckw = kw.toLowerCase();
        list = list.filter(i =>
          i.invoiceNo?.toLowerCase().includes(lckw) ||
          i.orderNo?.toLowerCase().includes(lckw) ||
          i.title?.toLowerCase().includes(lckw)
        );
      }
      setInvoices(list);
    } catch (err: any) {
      console.error('Failed to load invoices:', err);
    } finally {
      setInvoicesLoading(false);
      invoicesLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (view === 'orders') {
      loadOrders();
    } else {
      loadInvoices();
    }
  }, [view, loadOrders, loadInvoices]);

  const handleRequestInvoice = (order: Order) => {
    invoiceForm.resetFields();
    invoiceForm.setFieldsValue({
      orderNo: order.orderNo,
      billingType: 'PERSONAL',
      content: '信息技术服务费',
    });
    setInvoiceModalVisible(true);
  };

  const handleInvoiceSubmit = async () => {
    try {
      const values = await invoiceForm.validateFields();
      setInvoiceSubmitting(true);
      await api.post('/invoices', values);
      message.success(t('menu.billingNamespace.invoiceSuccess'));
      setInvoiceModalVisible(false);
      loadInvoices();
      loadOrders();
    } catch (err: any) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        message.error(t('menu.billingNamespace.submitFailed'));
      }
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  const handleViewInvoice = (record: Invoice) => {
    setInvoiceDetailRecord(record);
    setInvoiceDetailVisible(true);
  };

  const orderColumns = [
    {
      title: t('menu.billingNamespace.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (v: string) => <span className="billing-order-no">{v}</span>,
    },
    { title: t('menu.billingNamespace.plan'), dataIndex: 'planName', key: 'planName' },
    {
      title: t('menu.billingNamespace.amountPayable'),
      key: 'amount',
      render: (_: any, r: Order) => (
        <span className="billing-amount">
          ¥{r.finalAmount?.toFixed(2) ?? '0.00'}
          {r.discountAmount > 0 && <span className="billing-original"> ¥{r.amount?.toFixed(2)}</span>}
        </span>
      ),
    },
    {
      title: t('menu.billingNamespace.status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (s: number) => {
        const st = ORDER_STATUS[s] || { color: 'default', text: t('menu.billingNamespace.statusUnknown') };
        return <Tag color={st.color}>{st.text}</Tag>;
      },
    },
    {
      title: t('menu.billingNamespace.paymentMethod'),
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (m?: string) => m || '-',
    },
    {
      title: t('menu.billingNamespace.validityPeriod'),
      key: 'period',
      render: (_: any, r: Order) => {
        if (!r.startTime || !r.endTime) return '-';
        return `${new Date(r.startTime).toLocaleDateString()} ~ ${new Date(r.endTime).toLocaleDateString()}`;
      },
    },
    {
      title: t('menu.billingNamespace.orderTime'),
      dataIndex: 'createTime',
      key: 'createTime',
      render: (t?: string) => t ? new Date(t).toLocaleString() : '-',
    },
    {
      title: t('menu.billingNamespace.action'),
      key: 'actions',
      width: 100,
      render: (_: any, r: Order) =>
        r.status === 2 && (r.invoiceStatus === undefined || r.invoiceStatus === null) ? (
          <Button type="link" size="small" onClick={() => handleRequestInvoice(r)}>{t('menu.billingNamespace.requestInvoice')}</Button>
        ) : (
          <Tooltip title={t('menu.billingNamespace.invoiceRequested')}>
            <Button type="link" size="small" disabled>{t('menu.billingNamespace.invoiceRequestedBtn')}</Button>
          </Tooltip>
        ),
    },
  ];

  const invoiceColumns = [
    {
      title: t('menu.billingNamespace.invoiceNo'),
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 180,
      render: (v: string) => <span className="billing-order-no">{v}</span>,
    },
    {
      title: t('menu.billingNamespace.relatedOrder'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (v: string) => <span className="billing-order-no">{v}</span>,
    },
    { title: t('menu.billingNamespace.invoiceTitle'), dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: t('menu.billingNamespace.type'),
      dataIndex: 'billingType',
      key: 'billingType',
      width: 80,
      render: (val: string) => val === 'ENTERPRISE' ? t('menu.billingNamespace.enterprise') : t('menu.billingNamespace.personal'),
    },
    {
      title: t('menu.billingNamespace.amount'),
      key: 'amount',
      width: 90,
      render: (_: any, r: Invoice) => <span className="billing-amount">¥{r.amount?.toFixed(2) ?? '0.00'}</span>,
    },
    {
      title: t('menu.billingNamespace.status'),
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: number) => {
        const st = INVOICE_STATUS[s] || { color: 'default', text: t('menu.billingNamespace.statusUnknown') };
        return <Tag color={st.color}>{st.text}</Tag>;
      },
    },
    {
      title: t('menu.billingNamespace.applyTime'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (t?: string) => t ? new Date(t).toLocaleString() : '-',
    },
    {
      title: t('menu.billingNamespace.action'),
      key: 'actions',
      width: 80,
      render: (_: any, r: Invoice) => (
        <Button type="link" size="small" onClick={() => handleViewInvoice(r)}>{t('menu.billingNamespace.details')}</Button>
      ),
    },
  ];

  return (
    <div className="billing-container">
      <div className="billing-topbar">
        <div className="billing-topbar-left">
          <h1 className="billing-title">{t('menu.billingNamespace.pageTitle')}</h1>
          <div className="billing-view-switcher">
            <button
              className={`billing-view-btn ${view === 'orders' ? 'active' : ''}`}
              onClick={() => setView('orders')}
            >
              {t('menu.billingNamespace.orders')}
            </button>
            <button
              className={`billing-view-btn ${view === 'invoices' ? 'active' : ''}`}
              onClick={() => setView('invoices')}
            >
              {t('menu.billingNamespace.invoices')}
            </button>
          </div>
        </div>
      </div>

      {view === 'orders' ? (
        <>
          <Card className="billing-filter-card">
            <div className="billing-filter-row">
              <Input
                placeholder={t('menu.billingNamespace.searchOrders')}
                value={orderKeyword}
                onChange={e => { setOrderKeyword(e.target.value); }}
                allowClear
                className="billing-search"
              />
              <Select
                placeholder={t('menu.billingNamespace.filterStatus')}
                value={orderStatusFilter}
                onChange={v => setOrderStatusFilter(v)}
                allowClear
                className="billing-select"
              >
                <Select.Option value={1}>{t('menu.billingNamespace.statusPending')}</Select.Option>
                <Select.Option value={2}>{t('menu.billingNamespace.statusPaid')}</Select.Option>
                <Select.Option value={3}>{t('menu.billingNamespace.statusCompleted')}</Select.Option>
                <Select.Option value={4}>{t('menu.billingNamespace.statusCancelled')}</Select.Option>
                <Select.Option value={5}>{t('menu.billingNamespace.statusRefunded')}</Select.Option>
              </Select>
              <span className="billing-count">{t('menu.billingNamespace.totalCount').replace('{count}', String(orders.length))}</span>
            </div>
          </Card>

          <Card className="billing-table-card">
            {ordersLoading ? (
              <div className="billing-loading">{t('menu.billingNamespace.loading')}</div>
            ) : orders.length === 0 ? (
              <Empty description={t('menu.billingNamespace.noOrders')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                columns={orderColumns}
                dataSource={orders}
                rowKey="id"
                pagination={{ pageSize: 10, showSizeChanger: false }}
                className="billing-table"
              />
            )}
          </Card>
        </>
      ) : (
        <>
          <Card className="billing-filter-card">
            <div className="billing-filter-row">
              <Input
                placeholder={t('menu.billingNamespace.searchInvoices')}
                value={invoiceKeyword}
                onChange={e => { setInvoiceKeyword(e.target.value); }}
                allowClear
                className="billing-search"
              />
              <span className="billing-count">{t('menu.billingNamespace.totalCount').replace('{count}', String(invoices.length))}</span>
            </div>
          </Card>

          <Card className="billing-table-card">
            {invoicesLoading ? (
              <div className="billing-loading">{t('menu.billingNamespace.loading')}</div>
            ) : invoices.length === 0 ? (
              <Empty description={t('menu.billingNamespace.noInvoices')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                columns={invoiceColumns}
                dataSource={invoices}
                rowKey="id"
                pagination={{ pageSize: 10, showSizeChanger: false }}
                className="billing-table"
              />
            )}
          </Card>
        </>
      )}

      <Modal
        title={t('menu.billingNamespace.requestInvoiceModal')}
        open={invoiceModalVisible}
        className="billing-modal"
        onCancel={() => setInvoiceModalVisible(false)}
        onOk={handleInvoiceSubmit}
        okText={t('menu.billingNamespace.submitApplication')}
        cancelText={t('menu.billingNamespace.cancel')}
        confirmLoading={invoiceSubmitting}
        width={520}
        destroyOnHidden
      >
        <Form form={invoiceForm} layout="vertical" initialValues={{ billingType: 'PERSONAL', content: t('menu.billingNamespace.contentDefault') }}>
          <Form.Item label={t('menu.billingNamespace.relatedOrderField')} name="orderNo">
            <Input disabled />
          </Form.Item>

          <Form.Item label={t('menu.billingNamespace.invoiceType')} name="billingType">
            <Radio.Group>
              <Radio value="PERSONAL">{t('menu.billingNamespace.personal')}</Radio>
              <Radio value="ENTERPRISE">{t('menu.billingNamespace.enterprise')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label={t('menu.billingNamespace.invoiceTitleField')} name="title" rules={[{ required: true, message: t('menu.billingNamespace.invoiceTitlePlaceholder') }]}>
            <Input placeholder={t('menu.billingNamespace.invoiceTitlePlaceholder')} maxLength={200} />
          </Form.Item>

          <Form.Item label={t('menu.billingNamespace.taxRegistrationNo')} name="taxNo">
            <Input placeholder={t('menu.billingNamespace.taxNoPlaceholder')} maxLength={50} />
          </Form.Item>

          <Form.Item label={t('menu.billingNamespace.invoiceContent')} name="content">
            <Select>
              <Select.Option value="信息技术服务费">{t('menu.billingNamespace.contentIT')}</Select.Option>
              <Select.Option value="技术服务费">{t('menu.billingNamespace.contentTech')}</Select.Option>
              <Select.Option value="软件服务费">{t('menu.billingNamespace.contentSoftware')}</Select.Option>
            </Select>
          </Form.Item>

          <div className="billing-section-divider">{t('menu.billingNamespace.recipientInfo')}</div>

          <Form.Item label={t('menu.billingNamespace.recipientName')} name="recipientName">
            <Input placeholder={t('menu.billingNamespace.recipientNamePlaceholder')} maxLength={100} />
          </Form.Item>

          <Form.Item label={t('menu.billingNamespace.recipientPhone')} name="recipientPhone">
            <Input placeholder={t('menu.billingNamespace.recipientPhonePlaceholder')} maxLength={20} />
          </Form.Item>

          <Form.Item label={t('menu.billingNamespace.recipientAddress')} name="recipientAddress">
            <Input.TextArea placeholder={t('menu.billingNamespace.recipientAddressPlaceholder')} rows={2} maxLength={500} />
          </Form.Item>

          <div className="billing-tip">
            {t('menu.billingNamespace.invoiceTip')}
          </div>
        </Form>
      </Modal>

      <Modal
        title={t('menu.billingNamespace.invoiceDetails')}
        open={invoiceDetailVisible}
        className="billing-modal"
        onCancel={() => setInvoiceDetailVisible(false)}
        cancelText={t('menu.billingNamespace.cancel')}
        footer={null}
        width={480}
      >
        {invoiceDetailRecord && (
          <div className="billing-invoice-detail">
            <div className="billing-detail-row">
              <span className="billing-detail-label">{t('menu.billingNamespace.invoiceNo')}</span>
              <span className="billing-detail-value billing-order-no">{invoiceDetailRecord.invoiceNo}</span>
            </div>
            <div className="billing-detail-row">
              <span className="billing-detail-label">{t('menu.billingNamespace.relatedOrder')}</span>
              <span className="billing-detail-value billing-order-no">{invoiceDetailRecord.orderNo}</span>
            </div>
            <div className="billing-detail-row">
              <span className="billing-detail-label">{t('menu.billingNamespace.invoiceType')}</span>
              <span className="billing-detail-value">{invoiceDetailRecord.billingType === 'ENTERPRISE' ? t('menu.billingNamespace.enterprise') : t('menu.billingNamespace.personal')}</span>
            </div>
            <div className="billing-detail-row">
              <span className="billing-detail-label">{t('menu.billingNamespace.invoiceTitle')}</span>
              <span className="billing-detail-value">{invoiceDetailRecord.title}</span>
            </div>
            {invoiceDetailRecord.taxNo && (
              <div className="billing-detail-row">
                <span className="billing-detail-label">{t('menu.billingNamespace.taxNo')}</span>
                <span className="billing-detail-value">{invoiceDetailRecord.taxNo}</span>
              </div>
            )}
            {invoiceDetailRecord.content && (
              <div className="billing-detail-row">
                <span className="billing-detail-label">{t('menu.billingNamespace.content')}</span>
                <span className="billing-detail-value">{invoiceDetailRecord.content}</span>
              </div>
            )}
            <div className="billing-detail-row">
              <span className="billing-detail-label">{t('menu.billingNamespace.invoiceAmount')}</span>
              <span className="billing-detail-value billing-amount-lg">¥{invoiceDetailRecord.amount?.toFixed(2)}</span>
            </div>
            <div className="billing-detail-row">
              <span className="billing-detail-label">{t('menu.billingNamespace.invoiceStatus')}</span>
              <span className="billing-detail-value">
                <Tag color={INVOICE_STATUS[invoiceDetailRecord.status]?.color}>
                  {INVOICE_STATUS[invoiceDetailRecord.status]?.text}
                </Tag>
              </span>
            </div>
            {invoiceDetailRecord.reviewComment && (
              <div className="billing-detail-row">
                <span className="billing-detail-label">{t('menu.billingNamespace.reviewComment')}</span>
                <span className="billing-detail-value">{invoiceDetailRecord.reviewComment}</span>
              </div>
            )}
            {invoiceDetailRecord.recipientName && (
              <div className="billing-detail-row">
                <span className="billing-detail-label">{t('menu.billingNamespace.recipient')}</span>
                <span className="billing-detail-value">{invoiceDetailRecord.recipientName} {invoiceDetailRecord.recipientPhone || ''}</span>
              </div>
            )}
            {invoiceDetailRecord.recipientAddress && (
              <div className="billing-detail-row">
                <span className="billing-detail-label">{t('menu.billingNamespace.recipientAddressField')}</span>
                <span className="billing-detail-value">{invoiceDetailRecord.recipientAddress}</span>
              </div>
            )}
            <div className="billing-detail-row">
              <span className="billing-detail-label">{t('menu.billingNamespace.applicationTime')}</span>
              <span className="billing-detail-value">{new Date(invoiceDetailRecord.createTime).toLocaleString()}</span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
